import { getMemberCycleRemaining, getMemberUsedBorrowedQuota } from '@shared/member-quota';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  LogOut, 
  Search, 
  Plus, 
  Loader2,
  Users,
  History,
  Calendar,
  Package,
  BookOpen,
  Clock,
  Settings,
  MessageCircle,
  Shield,
  GripVertical,
  Trash2,
  Eye,
  RefreshCw,
  Ticket,
  Ban,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { createMember, updateMember, getRedemptions, getMemberTypeConfigs, createMemberTypeConfig, updateMemberTypeConfig, deleteMemberTypeConfig, getAdminMemberDetail, listRedeemCodeBatches, updateOrderNo } from '@/api/member';
import { adminLogout, getAdminContactConfig, updateAdminContactConfig, getAdminLoginHistory, updateAdminPassword } from '@/api/admin';
import { uploadFile } from '@/components/business-ui/api/files/service';
import { TopicAdminPanel } from './TopicAdminPanel';
import { AnalyticsPanel } from './AnalyticsPanel';
import { RedeemBatchPanel } from './RedeemBatchPanel';
import { QuickCodePanel } from './QuickCodePanel';
import { MemberListView } from './MemberListView';
import { RevokeMemberDialog } from './RevokeMemberDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { getCycleDateRange } from '@/utils/cycle-dates';
import { ADMIN_LOGIN_PATH } from '@/config/admin-routes';
import { validateAdminPasswordForm } from '@/lib/admin-password-validation';
import { completeAdminLogout } from '@/lib/admin-logout';
import type { Member, RedemptionRecord, MemberTypeConfig, ContactConfig, AdminLoginHistoryRecord, MemberAdminDetailResp, RedeemCodeBatch } from '@shared/api.interface';
import { MEMBER_STATUS_LABELS } from '@shared/api.interface';
import { isValidOrderNo, isValidPasscode, normalizePasscode } from '@shared/input-validation';
import { useMemberList } from './useMemberList';
import { QuotaAdjustDialog } from './QuotaAdjustDialog';
import { extractApiErrorMessage } from './api-error';

const ADMIN_NAV_GROUPS: Array<{
  id: string;
  label: string;
  tabs: Array<{ id: string; label: string; icon: LucideIcon }>;
}> = [
  {
    id: 'members',
    label: '会员与权益',
    tabs: [
      { id: 'quick-code', label: '快捷发码', icon: Zap },
      { id: 'members', label: '会员管理', icon: Users },
      { id: 'redeem-batches', label: '兑换码批次', icon: Ticket },
      { id: 'type-configs', label: '会员类型', icon: Settings },
      { id: 'redemptions', label: '领取记录', icon: History },
    ],
  },
  {
    id: 'content',
    label: '内容运营',
    tabs: [{ id: 'topics', label: '主题管理', icon: BookOpen },{ id: 'analytics', label: '运营数据', icon: Eye }],
  },
  {
    id: 'settings',
    label: '系统设置',
    tabs: [
      { id: 'contact-config', label: '客服配置', icon: MessageCircle },
      { id: 'security', label: '安全设置', icon: Shield },
      { id: 'login-history', label: '登录记录', icon: Shield },
    ],
  },
];

const MEMBER_STATUS_BADGE_CLASS: Record<Member['status'], string> = {
  active: 'bg-claimed-soft text-claimed',
  expired: 'bg-muted text-muted-foreground',
  frozen: 'bg-red-50 text-red-700',
  inactive: 'bg-stone-100 text-stone-600',
  revoked: 'bg-red-100 text-red-800',
};

// 未激活、已作废由系统流程产生，不能在编辑资料里直接改状态
const isSystemStatus = (status: Member['status']) => status === 'inactive' || status === 'revoked';

const TAB_DESCRIPTIONS: Record<string, string> = {
  'quick-code': '微信上卖出次卡时，一键生成一个码，复制话术发给客户',
  members: '按兑换码管理会员资料、额度与有效期',
  'redeem-batches': '批量生成兑换码，导出给发货软件自动发货',
  'type-configs': '设置每种会员的额度规则，新增会员时选择',
  redemptions: '会员的领取和管理员额度调整都记在这里',
  'contact-config': '控制前台“联系客服”入口出现在哪里、显示什么',
  security: '修改管理员密码',
  'login-history': '管理后台的每次登录尝试',
};

// 主题管理、运营数据面板自带标题和操作区，不再重复显示页面标题
const PANELS_WITH_OWN_HEADER = new Set(['topics', 'analytics']);

const brandLogoSrc =
  (typeof document !== 'undefined' && document.querySelector('link[rel="icon"]')?.getAttribute('href')) ||
  '/brand-logo.svg';

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  // 手机上打开后台默认是快捷发码（人不在电脑前时最常用），电脑上仍默认会员管理
  const [activeTab, setActiveTab] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches ? 'quick-code' : 'members'
  );
  const activeNavGroup = ADMIN_NAV_GROUPS.find((group) =>
    group.tabs.some((tab) => tab.id === activeTab)
  ) ?? ADMIN_NAV_GROUPS[0];
  const activeTabLabel =
    activeNavGroup.tabs.find((tab) => tab.id === activeTab)?.label ?? '';
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // 检查登录状态
  useEffect(() => {
    const token = sessionStorage.getItem('admin_token');
    if (!token) {
      navigate(ADMIN_LOGIN_PATH);
    } else {
      setIsAuthenticated(true);
    }
  }, [navigate]);
  
  // 会员管理状态
  const { members, memberSearch, setMemberSearch, isLoadingMembers, memberError, fetchMembers,
    memberTotal, memberPage, memberTotalPages, setMemberPage, memberQuotaFilter, setMemberQuotaFilter,
    memberStatusFilters, toggleMemberStatusFilter, onMemberCompositionStart, onMemberCompositionEnd,
    memberBatchFilter, setMemberBatchFilter,
  } = useMemberList(isAuthenticated);
  const [batchOptions, setBatchOptions] = useState<RedeemCodeBatch[]>([]);
  const [revokingDetail, setRevokingDetail] = useState<MemberAdminDetailResp | null>(null);
  const [editingOrderNo, setEditingOrderNo] = useState('');
  const [showAddMember, setShowAddMember] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [adjustingMember, setAdjustingMember] = useState<Member | null>(null);
  const [isUpdatingMember, setIsUpdatingMember] = useState(false);
  const [memberDetail, setMemberDetail] = useState<MemberAdminDetailResp | null>(null);
  const [isMemberDetailOpen, setIsMemberDetailOpen] = useState(false);
  const [isLoadingMemberDetail, setIsLoadingMemberDetail] = useState(false);

  
  // 会员类型配置
  const [memberTypeConfigs, setMemberTypeConfigs] = useState<MemberTypeConfig[]>([]);
  const [isLoadingTypeConfigs, setIsLoadingTypeConfigs] = useState(false);
  
  // 会员类型管理状态
  const [showTypeConfigTab, setShowTypeConfigTab] = useState(false);
  const [typeConfigTab, setTypeConfigTab] = useState<'monthly' | 'total'>('monthly');
  const [showAddTypeConfig, setShowAddTypeConfig] = useState(false);
  const [editingTypeConfig, setEditingTypeConfig] = useState<MemberTypeConfig | null>(null);
  const [deletingTypeConfig, setDeletingTypeConfig] = useState<MemberTypeConfig | null>(null);

  const getTypeQuotaLabel = (config: MemberTypeConfig): string => {
    if (config.quota_mode === 'total') {
      return `年度总次数${config.total_quota}，单日上限${config.daily_limit}`;
    }
    return `月配额${config.monthly_quota}，单日上限${config.daily_limit}`;
  };

  const getMemberQuotaUsagePercent = (member: Member): number => {
    const used = member.quota_mode === 'total' ? member.total_redemptions : member.cycle_used;
    const limit = member.quota_mode === 'total'
      ? member.total_quota ?? 0
      : member.monthly_quota + (member.cycle_bonus ?? 0) - (member.cycle_deduction ?? 0);
    if (limit <= 0) return used > 0 ? 100 : 0;
    return Math.min(100, Math.round((used / limit) * 100));
  };

  const getMemberQuotaUsageLabel = (member: Member): string => {
    if (member.quota_mode === 'total') {
      return `${member.total_redemptions}/${member.total_quota ?? 0}`;
    }
    return `${member.cycle_used}/${member.monthly_quota}`;
  };

  // 与用户端会员权益口径一致的配额计算
  const getMemberRemainingLabel = (member: Member): string => {
    if (member.quota_mode === 'total') {
      const remaining = Math.max(0, (member.total_quota ?? 0) - member.total_redemptions);
      return `${remaining} 次`;
    }
    const remaining = getMemberCycleRemaining(member);
    return `${remaining} 次`;
  };

  const getMemberCycleRangeLabel = (member: Member): string => {
    if (member.quota_mode === 'total') return '年度总额度，不按周期恢复';
    const cycle = getCycleDateRange(member.cycle_start_date);
    if (!cycle) return '-';
    return `${cycle.startLabel} - ${cycle.endLabel}（下次恢复 ${cycle.nextResetDate}）`;
  };

  const typeConfigMap = useMemo(() => {
    return new Map(memberTypeConfigs.map((config) => [config.name, config]));
  }, [memberTypeConfigs]);

  // 新增会员类型表单
  const [newTypeConfig, setNewTypeConfig] = useState({
    name: '',
    monthly_quota: 3,
    daily_limit: '' as string | number,
    validity_months: 12,
    quota_mode: 'monthly' as 'monthly' | 'total',
    total_quota: '' as string | number,
  });
  
  // 新增会员表单
  const [newMember, setNewMember] = useState({
    passcode: '',
    member_type: '',
    start_date: new Date().toISOString().split('T')[0],
    source_channel: '',
    remark: '',
  });
  const normalizedNewMemberPasscode = normalizePasscode(newMember.passcode);
  const newMemberPasscodeError = newMember.passcode && !isValidPasscode(normalizedNewMemberPasscode)
    ? '兑换码必须为 6–32 位数字或英文字母；最外侧空白会自动去除，内部空格、中文和特殊符号不支持'
    : '';
  
  // 领取记录状态
  const [redemptions, setRedemptions] = useState<RedemptionRecord[]>([]);
  const [redemptionSearch, setRedemptionSearch] = useState('');
  const [isLoadingRedemptions, setIsLoadingRedemptions] = useState(false);

  // 客服配置状态
  const [contactConfig, setContactConfig] = useState<ContactConfig>({
    enabled: false,
    show_header_entry: true,
    method: 'qr',
    title: '购买 / 续费 / 单独获取',
    description: '支持会员开通、续费、单独获取主题、找回兑换码',
    detail_description: '支持兑换码领取、单独获取、续费咨询',
    passcode_hint: '兑换码通常为订单号，需获取可联系客服',
    button_text: '联系客服',
    wechat_id: '',
    qr_image_url: '',
    link_url: '',
  });
  const [isLoadingContactConfig, setIsLoadingContactConfig] = useState(false);
  const [isSavingContactConfig, setIsSavingContactConfig] = useState(false);
  const [isUploadingQrImage, setIsUploadingQrImage] = useState(false);
  const qrImageInputRef = useRef<HTMLInputElement | null>(null);

  // 登录历史状态
  const [loginHistory, setLoginHistory] = useState<AdminLoginHistoryRecord[]>([]);
  const [isLoadingLoginHistory, setIsLoadingLoginHistory] = useState(false);
  const [loginHistoryPage, setLoginHistoryPage] = useState(1);
  const [loginHistoryTotal, setLoginHistoryTotal] = useState(0);
  const LOGIN_HISTORY_PAGE_SIZE = 20;

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  // 获取会员类型配置
  const fetchMemberTypeConfigs = useCallback(async () => {
    setIsLoadingTypeConfigs(true);
    try {
      const result = await getMemberTypeConfigs();
      setMemberTypeConfigs(result.items);
      // 设置默认选中第一个启用的类型
      if (result.items.length > 0 && !newMember.member_type) {
        setNewMember(prev => ({ ...prev, member_type: result.items[0].name }));
      }
    } catch (error) {
      toast.error('获取会员类型配置失败');
    } finally {
      setIsLoadingTypeConfigs(false);
    }
  }, []);



  const handleOpenMemberDetail = async (passcode: string) => {
    setIsMemberDetailOpen(true);
    setMemberDetail(null);
    setIsLoadingMemberDetail(true);
    try {
      const result = await getAdminMemberDetail(passcode);
      setMemberDetail(result);
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '获取会员详情失败'));
      setIsMemberDetailOpen(false);
    } finally {
      setIsLoadingMemberDetail(false);
    }
  };

  const handleOpenEditMember = (member: Member) => {
    setEditingMember(member);
    setEditingOrderNo(member.order_no || '');
  };

  // 会员列表的批次筛选项
  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'members') return;
    listRedeemCodeBatches().then(result => setBatchOptions(result.items)).catch(() => undefined);
  }, [isAuthenticated, activeTab]);

  // 会员列表直接进入退款作废：先取详情（已领主题、剩余次数）再打开确认弹窗
  const handleOpenRevoke = async (passcode: string) => {
    try {
      setRevokingDetail(await getAdminMemberDetail(passcode));
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '获取会员详情失败'));
    }
  };

  const handleViewBatch = (batchId: number) => {
    setMemberBatchFilter(batchId);
    setActiveTab('members');
  };

  // 获取领取记录
  const fetchRedemptions = useCallback(async () => {
    setIsLoadingRedemptions(true);
    try {
      const result = await getRedemptions({ passcode: redemptionSearch });
      setRedemptions(result.items);
    } catch (error) {
      toast.error('获取领取记录失败');
    } finally {
      setIsLoadingRedemptions(false);
    }
  }, [redemptionSearch]);

  // 获取客服配置
  const fetchContactConfig = useCallback(async () => {
    setIsLoadingContactConfig(true);
    try {
      const result = await getAdminContactConfig();
      setContactConfig(result);
    } catch (error) {
      toast.error('获取客服配置失败');
    } finally {
      setIsLoadingContactConfig(false);
    }
  }, []);

  // 获取登录历史
  const fetchLoginHistory = useCallback(async (page: number = 1) => {
    setIsLoadingLoginHistory(true);
    try {
      const result = await getAdminLoginHistory(page, LOGIN_HISTORY_PAGE_SIZE);
      setLoginHistory(result.items);
      setLoginHistoryTotal(result.total);
      setLoginHistoryPage(page);
    } catch (error) {
      toast.error('获取登录记录失败');
    } finally {
      setIsLoadingLoginHistory(false);
    }
  }, []);

  // 当切换到登录记录 tab 时加载数据
  useEffect(() => {
    if (isAuthenticated && activeTab === 'login-history') {
      fetchLoginHistory(1);
    }
  }, [isAuthenticated, activeTab, fetchLoginHistory]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchMemberTypeConfigs();
      fetchContactConfig();
    }
  }, [isAuthenticated, fetchMemberTypeConfigs, fetchContactConfig]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchRedemptions();
    }
  }, [isAuthenticated, fetchRedemptions]);

  // 退出登录
  const handleLogout = async () => {
    await completeAdminLogout({
      requestServerLogout: adminLogout,
      clearLocalToken: () => sessionStorage.removeItem('admin_token'),
      redirectToLogin: () => navigate(ADMIN_LOGIN_PATH),
      notifyServerLogoutSuccess: () => toast.success('已安全退出'),
      notifyServerLogoutUnconfirmed: () =>
        toast.warning('本地已退出，但服务端撤销未确认，请稍后重试或联系管理员'),
    });
  };

  // 新增会员
  const handleAddMember = async () => {
    if (!normalizedNewMemberPasscode) {
      toast.error('请输入兑换码');
      return;
    }
    if (newMemberPasscodeError) {
      toast.error(newMemberPasscodeError);
      return;
    }

    try {
      await createMember({
        passcode: normalizedNewMemberPasscode,
        member_type: newMember.member_type,
        start_date: newMember.start_date,
        source_channel: newMember.source_channel || undefined,
        remark: newMember.remark || undefined,
      });
      toast.success('会员创建成功');
      setShowAddMember(false);
      setNewMember({
        passcode: '',
        member_type: memberTypeConfigs[0]?.name || '',
        start_date: new Date().toISOString().split('T')[0],
        source_channel: '',
        remark: '',
      });
      fetchMembers();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '创建会员失败'));
    }
  };

  // 更新会员资料（状态、渠道、备注）；额度调整在独立的调整额度弹窗里完成
  const handleUpdateMember = async () => {
    if (!editingMember || isUpdatingMember) return;
    const orderNo = editingOrderNo.trim();
    if (orderNo && !isValidOrderNo(orderNo)) {
      toast.error('订单号只能包含字母、数字、下划线或连字符，最长 64 位');
      return;
    }
    setIsUpdatingMember(true);
    try {
      const { status } = editingMember;
      await updateMember({
        passcode: editingMember.passcode,
        status: isSystemStatus(status) ? undefined : status,
        source_channel: editingMember.source_channel,
        remark: editingMember.remark,
      });
      if (orderNo !== (editingMember.order_no || '')) {
        await updateOrderNo({ passcode: editingMember.passcode, order_no: orderNo });
      }
      toast.success('会员资料已保存');
      setEditingMember(null);
      await fetchMembers();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '更新会员失败'));
    } finally {
      setIsUpdatingMember(false);
    }
  };

  // 新增会员类型
  const handleAddTypeConfig = async () => {
    if (!newTypeConfig.name.trim()) {
      toast.error('请输入类型名称');
      return;
    }
    if (newTypeConfig.quota_mode === 'total') {
      const totalQuota = parseInt(String(newTypeConfig.total_quota), 10);
      if (!totalQuota || totalQuota <= 0) {
        toast.error('请输入年度总次数');
        return;
      }
      const dailyLimit = parseInt(String(newTypeConfig.daily_limit), 10);
      if (!dailyLimit || dailyLimit <= 0) {
        toast.error('请输入单日配额');
        return;
      }
    }

    try {
      await createMemberTypeConfig({
        name: newTypeConfig.name.trim(),
        monthly_quota: newTypeConfig.monthly_quota,
        daily_limit: parseInt(String(newTypeConfig.daily_limit), 10) || 1,
        validity_months: newTypeConfig.validity_months,
        status: '启用',
        quota_mode: newTypeConfig.quota_mode,
        total_quota: parseInt(String(newTypeConfig.total_quota), 10) || 0,
      });
      toast.success('会员类型创建成功');
      setShowAddTypeConfig(false);
      setNewTypeConfig({
        name: '',
        monthly_quota: 3,
        daily_limit: '',
        validity_months: 12,
        quota_mode: 'monthly',
        total_quota: '',
      });
      fetchMemberTypeConfigs();
    } catch (error) {
      toast.error('创建会员类型失败');
    }
  };

  // 更新会员类型
  const handleUpdateTypeConfig = async () => {
    if (!editingTypeConfig) return;

    try {
      await updateMemberTypeConfig(editingTypeConfig.id, {
        name: editingTypeConfig.name,
        monthly_quota: editingTypeConfig.monthly_quota,
        daily_limit: editingTypeConfig.daily_limit,
        validity_months: editingTypeConfig.validity_months,
        status: editingTypeConfig.status,
        quota_mode: editingTypeConfig.quota_mode,
        total_quota: editingTypeConfig.total_quota,
      });
      toast.success('会员类型更新成功');
      setEditingTypeConfig(null);
      fetchMemberTypeConfigs();
    } catch (error) {
      toast.error('更新会员类型失败');
    }
  };

  // 删除会员类型（实际为停用）
  const handleDeleteTypeConfig = (config: MemberTypeConfig) => {
    setDeletingTypeConfig(config);
  };

  const confirmDeleteTypeConfig = async () => {
    if (!deletingTypeConfig) return;

    try {
      await deleteMemberTypeConfig(deletingTypeConfig.id);
      toast.success('会员类型已停用');
      setDeletingTypeConfig(null);
      fetchMemberTypeConfigs();
    } catch (error) {
      toast.error('停用会员类型失败');
    }
  };

  // 保存客服配置
  const handleSaveContactConfig = async () => {
    setIsSavingContactConfig(true);
    try {
      const saved = await updateAdminContactConfig(contactConfig);
      setContactConfig(saved);
      toast.success('客服配置已保存');
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '保存客服配置失败'));
    } finally {
      setIsSavingContactConfig(false);
    }
  };

  const handleQrImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploadingQrImage(true);
    try {
      const result = await uploadFile(file);
      setContactConfig((prev) => ({ ...prev, qr_image_url: result.url }));
      toast.success('二维码上传成功');
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '二维码上传失败'));
    } finally {
      setIsUploadingQrImage(false);
      event.target.value = '';
    }
  };

  const handleUpdateAdminPassword = async () => {
    const currentPassword = passwordForm.currentPassword.trim();
    const newPassword = passwordForm.newPassword.trim();
    const confirmPassword = passwordForm.confirmPassword.trim();

    if (!currentPassword) {
      toast.error('请输入当前密码');
      return;
    }
    const passwordValidationError = validateAdminPasswordForm(newPassword, confirmPassword);
    if (passwordValidationError) {
      toast.error(passwordValidationError);
      return;
    }

    setIsUpdatingPassword(true);
    try {
      await updateAdminPassword({
        currentPassword,
        newPassword,
      });
      toast.success('密码已修改，请重新登录');
      sessionStorage.removeItem('admin_token');
      navigate(ADMIN_LOGIN_PATH);
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '修改密码失败'));
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // 未认证时显示加载状态
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
      orientation="vertical"
      className="min-h-screen gap-0 bg-background md:flex-row"
    >
      {/* 桌面端左侧导航：按业务分组 */}
      <aside className="sticky top-0 hidden h-screen w-56 shrink-0 flex-col border-r border-border bg-white px-3 py-5 md:flex">
        <div className="mb-3 flex items-center gap-2.5 px-2">
          <img src={brandLogoSrc} alt="" className="h-8 w-8 rounded-lg object-cover" />
          <span className="text-[15px] font-bold text-foreground">管理后台</span>
        </div>
        <TabsList
          aria-label="后台功能导航"
          className="flex h-auto w-full flex-1 flex-col items-stretch justify-start gap-0 overflow-y-auto rounded-none bg-transparent p-0"
        >
          {ADMIN_NAV_GROUPS.map((group) => (
            <div key={group.id} className="pb-1">
              <div className="px-3 pb-1.5 pt-4 text-xs font-medium text-muted-foreground">{group.label}</div>
              {group.tabs.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="h-10 w-full flex-none justify-start gap-2.5 rounded-lg px-3 text-sm font-normal text-muted-foreground hover:bg-accent hover:text-foreground data-[state=active]:bg-orange-50 data-[state=active]:font-semibold data-[state=active]:text-orange-900 data-[state=active]:shadow-none"
                >
                  <tab.icon className="h-4 w-4 shrink-0" />
                  {tab.label}
                </TabsTrigger>
              ))}
            </div>
          ))}
        </TabsList>
        <div className="mt-3 flex items-center justify-between border-t border-border px-2 pt-3">
          <span className="text-[13px] text-muted-foreground">管理员</span>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="h-8 px-2 text-[13px] text-orange-800 hover:bg-orange-50">
            <LogOut className="mr-1 h-3.5 w-3.5" />
            退出登录
          </Button>
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        {/* 手机端顶部栏 */}
        <header className="border-b border-border bg-white md:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2.5">
              <img src={brandLogoSrc} alt="" className="h-8 w-8 rounded-lg object-cover" />
              <h1 className="text-base font-bold">管理后台</h1>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground">
              <LogOut className="mr-1 h-4 w-4" />
              退出
            </Button>
          </div>
        </header>

        {/* 主内容区 */}
        <div className="mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-7">
          <div className="mb-6 grid gap-3 sm:grid-cols-2 md:hidden">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="admin-nav-group">业务分组</label>
              <Select value={activeNavGroup.id} onValueChange={(groupId) => {
                const group = ADMIN_NAV_GROUPS.find((item) => item.id === groupId);
                if (group) setActiveTab(group.tabs[0].id);
              }}>
                <SelectTrigger id="admin-nav-group" aria-label="选择后台业务分组" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADMIN_NAV_GROUPS.map((group) => (
                    <SelectItem key={group.id} value={group.id}>{group.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="admin-nav-page">管理页面</label>
              <Select value={activeTab} onValueChange={setActiveTab}>
                <SelectTrigger id="admin-nav-page" aria-label="选择后台管理页面" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {activeNavGroup.tabs.map((tab) => (
                    <SelectItem key={tab.id} value={tab.id}>{tab.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* 页面标题 */}
          {!PANELS_WITH_OWN_HEADER.has(activeTab) && (
            <div className="mb-5">
              <h2 className="text-2xl font-bold text-foreground">{activeTabLabel}</h2>
              {TAB_DESCRIPTIONS[activeTab] && (
                <p className="mt-1 text-sm text-muted-foreground">{TAB_DESCRIPTIONS[activeTab]}</p>
              )}
            </div>
          )}

          <TabsContent value="quick-code">
            <QuickCodePanel memberTypeConfigs={memberTypeConfigs} isLoadingTypes={isLoadingTypeConfigs} />
          </TabsContent>
          <TabsContent value="redeem-batches">
            <RedeemBatchPanel memberTypeConfigs={memberTypeConfigs} onViewBatch={handleViewBatch} />
          </TabsContent>
          <TabsContent value="analytics"><AnalyticsPanel onOpenRedemptions={() => setActiveTab('redemptions')} onOpenMemberDetail={(passcode) => void handleOpenMemberDetail(passcode)} /></TabsContent>
          <TabsContent value="topics" className="space-y-4">
            <TopicAdminPanel />
          </TabsContent>

          {/* 会员管理 */}
          <TabsContent value="members" className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <div className="relative min-w-0 basis-full sm:basis-auto flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="搜索兑换码、订单号或备注…"
                  aria-label="搜索兑换码、订单号或备注"
                  maxLength={100}
                  onCompositionStart={onMemberCompositionStart}
                  onCompositionEnd={(e) => onMemberCompositionEnd(e.currentTarget.value)}
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button variant="outline" onClick={() => void fetchMembers()} disabled={isLoadingMembers}>
                <RefreshCw className={`mr-1 h-4 w-4 ${isLoadingMembers ? 'animate-spin' : ''}`} />
                刷新
              </Button>
              <Button onClick={() => setShowAddMember(true)}>
                <Plus className="w-4 h-4 mr-1" />
                新增会员
              </Button>
            </div>

            <div className="space-y-3 rounded-lg border bg-white p-3">
              <div>
                <p className="mb-2 text-sm font-medium text-foreground">配额模式筛选</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={memberQuotaFilter === 'all' ? 'default' : 'outline'}
                    onClick={() => setMemberQuotaFilter('all')}
                  >
                    全部
                  </Button>
                  <Button
                    size="sm"
                    variant={memberQuotaFilter === 'monthly' ? 'default' : 'outline'}
                    onClick={() => setMemberQuotaFilter('monthly')}
                  >
                    按月配额
                  </Button>
                  <Button
                    size="sm"
                    variant={memberQuotaFilter === 'total' ? 'default' : 'outline'}
                    onClick={() => setMemberQuotaFilter('total')}
                  >
                    年度总次数
                  </Button>
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium text-foreground">会员状态筛选</p>
                <div className="flex flex-wrap gap-4">
                  {(['active', 'frozen', 'expired', 'inactive', 'revoked'] as Array<Member['status']>).map((status) => (
                    <label key={status} className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                        checked={memberStatusFilters.includes(status)}
                        onChange={() => toggleMemberStatusFilter(status)}
                      />
                      <span>{MEMBER_STATUS_LABELS[status]}</span>
                    </label>
                  ))}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">未勾选状态时默认展示全部会员</p>
              </div>
              {batchOptions.length > 0 && (
                <div className="max-w-sm">
                  <label htmlFor="member-batch-filter" className="mb-2 block text-sm font-medium text-foreground">兑换码批次</label>
                  <Select
                    value={memberBatchFilter ? String(memberBatchFilter) : 'all'}
                    onValueChange={(value) => setMemberBatchFilter(value === 'all' ? undefined : Number(value))}
                  >
                    <SelectTrigger id="member-batch-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">全部（含手动创建）</SelectItem>
                      {batchOptions.map(batch => (
                        <SelectItem key={batch.id} value={String(batch.id)}>#{batch.id} {batch.product_label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {memberError ? (
              <div role="alert" className="rounded-lg border border-destructive p-4 text-destructive">
                <p>{memberError}</p>
                <Button variant="outline" className="mt-2" onClick={() => void fetchMembers()}>重试</Button>
              </div>
            ) : isLoadingMembers ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <MemberListView
                members={members}
                statusBadgeClass={MEMBER_STATUS_BADGE_CLASS}
                getUsagePercent={getMemberQuotaUsagePercent}
                getValidityMonths={(member) => typeConfigMap.get(member.member_type)?.validity_months}
                onOpenDetail={(passcode) => void handleOpenMemberDetail(passcode)}
                onAdjust={setAdjustingMember}
                onEdit={handleOpenEditMember}
                onRevoke={(passcode) => void handleOpenRevoke(passcode)}
              />
            )}
            <div aria-label="会员分页" className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <span aria-live="polite">共 {memberTotal} 条 · 第 {memberPage}/{memberTotalPages} 页</span>
              <div className="flex gap-2">
                <Button variant="outline" disabled={isLoadingMembers || !!memberError || memberPage <= 1}
                  onClick={() => setMemberPage(memberPage - 1)}>上一页</Button>
                <Button variant="outline" disabled={isLoadingMembers || !!memberError || memberPage >= memberTotalPages}
                  onClick={() => setMemberPage(memberPage + 1)}>下一页</Button>
              </div>
            </div>
          </TabsContent>

          {/* 会员类型管理 */}
          <TabsContent value="type-configs" className="space-y-4">
            {/* 内部分类 TAB */}
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <Button
                  variant={typeConfigTab === 'monthly' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTypeConfigTab('monthly')}
                >
                  按月配额
                </Button>
                <Button
                  variant={typeConfigTab === 'total' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTypeConfigTab('total')}
                >
                  年度总次数
                </Button>
              </div>
              <Button
                size="sm"
                onClick={() => {
                  setNewTypeConfig({
                    name: '',
                    monthly_quota: 3,
                    daily_limit: typeConfigTab === 'total' ? '' : 1,
                    validity_months: 12,
                    quota_mode: typeConfigTab,
                    total_quota: typeConfigTab === 'total' ? '' : 3,
                  });
                  setShowAddTypeConfig(true);
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                新增{typeConfigTab === 'total' ? '年度总次数' : '按月配额'}类型
              </Button>
            </div>

            {isLoadingTypeConfigs ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                {typeConfigTab === 'monthly' ? (
                  // 按月配额列表
                  <table className="w-full">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">类型名称</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">月配额</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">单日上限</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">有效期（月）</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">状态</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">操作</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {memberTypeConfigs.filter(c => c.quota_mode !== 'total').map((config) => (
                        <tr key={config.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm">{config.name}</td>
                          <td className="px-4 py-3 text-sm">{config.monthly_quota}次/月</td>
                          <td className="px-4 py-3 text-sm">{config.daily_limit}次/日</td>
                          <td className="px-4 py-3 text-sm">{config.validity_months}</td>
                          <td className="px-4 py-3 text-sm">
                            <Badge variant={config.status === '启用' ? 'default' : 'destructive'}>
                              {config.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingTypeConfig(config)}
                              >
                                编辑
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() => handleDeleteTypeConfig(config)}
                              >
                                停用
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  // 年度总次数列表
                  <table className="w-full">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">类型名称</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">年度总次数</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">单日配额</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">有效期（月）</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">状态</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">操作</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {memberTypeConfigs.filter(c => c.quota_mode === 'total').map((config) => (
                        <tr key={config.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm">{config.name}</td>
                          <td className="px-4 py-3 text-sm">{config.total_quota}次/年</td>
                          <td className="px-4 py-3 text-sm">{config.daily_limit}次/日</td>
                          <td className="px-4 py-3 text-sm">{config.validity_months}</td>
                          <td className="px-4 py-3 text-sm">
                            <Badge variant={config.status === '启用' ? 'default' : 'destructive'}>
                              {config.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingTypeConfig(config)}
                              >
                                编辑
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() => handleDeleteTypeConfig(config)}
                              >
                                停用
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {memberTypeConfigs.filter(c => typeConfigTab === 'monthly' ? c.quota_mode !== 'total' : c.quota_mode === 'total').length === 0 && (
                  <div className="text-center py-10 text-muted-foreground">
                    暂无{typeConfigTab === 'monthly' ? '按月配额' : '年度总次数'}类型配置
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* 领取记录 */}
          <TabsContent value="redemptions" className="space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="筛选兑换码..."
                  value={redemptionSearch}
                  onChange={(e) => setRedemptionSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {isLoadingRedemptions ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full">
                    <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">兑换码</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">主题名称</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">会员类型</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">领取时间</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">结果</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">拒绝原因</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {redemptions.map((record) => (
                      <tr key={record.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-mono">{record.passcode}</td>
                        <td className="px-4 py-3 text-sm">{record.topic_name}</td>
                        <td className="px-4 py-3 text-sm">
                          {record.member_type}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {new Date(record.redemption_time).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant={record.record_type === 'quota_adjustment' ? 'secondary' : record.result === 'success' ? 'default' : 'destructive'}>
                            {record.record_type === 'quota_adjustment' ? '管理员调整' : record.result === 'success' ? '成功' : '失败'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {record.record_type === 'quota_adjustment' ? (record.adjustment_note || '未填写调整说明') : (record.fail_reason || '-')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {redemptions.length === 0 && (
                  <div className="text-center py-10 text-muted-foreground">
                    暂无领取记录
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* 客服配置 */}
          <TabsContent value="contact-config" className="space-y-4">
            {isLoadingContactConfig ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
              <div className="space-y-5 rounded-2xl bg-white p-6 shadow-[0_0_0_1px_var(--border)]">
                <div className="flex items-center justify-between rounded-md border p-4">
                  <div>
                    <p className="text-sm font-medium">启用前台客服入口</p>
                    <p className="text-xs text-muted-foreground mt-1">控制详情弹窗、兑换码弹窗内的客服承接入口</p>
                  </div>
                  <Switch
                    checked={contactConfig.enabled}
                    onCheckedChange={(checked) =>
                      setContactConfig((prev) => ({ ...prev, enabled: checked }))
                    }
                  />
                </div>

                <div className="flex items-center justify-between rounded-md border p-4">
                  <div>
                    <p className="text-sm font-medium">登录按钮旁展示客服入口</p>
                    <p className="text-xs text-muted-foreground mt-1">仅控制首页顶部“登录”旁的客服入口，不影响详情/兑换码弹窗内的客服入口</p>
                  </div>
                  <Switch
                    checked={contactConfig.show_header_entry}
                    onCheckedChange={(checked) =>
                      setContactConfig((prev) => ({ ...prev, show_header_entry: checked }))
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">承接方式</label>
                  <Select
                    value={contactConfig.method}
                    onValueChange={(value: 'qr' | 'link') =>
                      setContactConfig((prev) => ({ ...prev, method: value }))
                    }
                  >
                    <SelectTrigger className="max-w-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="qr">二维码弹窗</SelectItem>
                      <SelectItem value="link">外部链接跳转</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">公开页标题</label>
                    <Input
                      value={contactConfig.title}
                      onChange={(e) =>
                        setContactConfig((prev) => ({ ...prev, title: e.target.value }))
                      }
                      placeholder="购买 / 续费 / 单独获取"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">按钮文案</label>
                    <Input
                      value={contactConfig.button_text}
                      onChange={(e) =>
                        setContactConfig((prev) => ({ ...prev, button_text: e.target.value }))
                      }
                      placeholder="联系客服"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">公开页说明</label>
                  <Input
                    value={contactConfig.description}
                    onChange={(e) =>
                      setContactConfig((prev) => ({ ...prev, description: e.target.value }))
                    }
                    placeholder="支持会员开通、续费、单独获取主题、找回兑换码"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">详情页说明</label>
                  <Input
                    value={contactConfig.detail_description}
                    onChange={(e) =>
                      setContactConfig((prev) => ({ ...prev, detail_description: e.target.value }))
                    }
                    placeholder="支持兑换码领取、单独获取、续费咨询"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">兑换码提示文案</label>
                  <Input
                    value={contactConfig.passcode_hint}
                    onChange={(e) =>
                      setContactConfig((prev) => ({ ...prev, passcode_hint: e.target.value }))
                    }
                    placeholder="兑换码通常为订单号，需获取可联系客服"
                  />
                </div>

                {contactConfig.method === 'qr' ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">微信号</label>
                      <Input
                        value={contactConfig.wechat_id}
                        onChange={(e) =>
                          setContactConfig((prev) => ({ ...prev, wechat_id: e.target.value }))
                        }
                        placeholder="例如：abc123"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">二维码图片地址</label>
                      <Input
                        value={contactConfig.qr_image_url}
                        onChange={(e) =>
                          setContactConfig((prev) => ({ ...prev, qr_image_url: e.target.value }))
                        }
                        placeholder="https://..."
                      />
                    </div>
                    <div className="md:col-span-2">
                      <input
                        ref={qrImageInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={handleQrImageUpload}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => qrImageInputRef.current?.click()}
                        disabled={isUploadingQrImage}
                      >
                        {isUploadingQrImage ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            上传中...
                          </>
                        ) : (
                          '上传二维码图片'
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">客服链接地址</label>
                    <Input
                      value={contactConfig.link_url}
                      onChange={(e) =>
                        setContactConfig((prev) => ({ ...prev, link_url: e.target.value }))
                      }
                      placeholder="https://..."
                    />
                  </div>
                )}

                <div className="flex justify-end">
                  <Button onClick={handleSaveContactConfig} disabled={isSavingContactConfig}>
                    {isSavingContactConfig ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        保存中...
                      </>
                    ) : (
                      '保存配置'
                    )}
                  </Button>
                </div>
              </div>
              <ContactPreview config={contactConfig} />
              </div>
            )}
          </TabsContent>

          <TabsContent value="security" className="space-y-4">
            <div className="rounded-lg bg-white p-6 shadow">
              <div className="mb-5">
                <h2 className="text-lg font-semibold">修改管理员密码</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  修改成功后会自动退出后台，请使用新密码重新登录。
                </p>
              </div>

              <div className="max-w-md space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium">当前密码</label>
                  <Input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) =>
                      setPasswordForm((prev) => ({
                        ...prev,
                        currentPassword: e.target.value,
                      }))
                    }
                    placeholder="请输入当前管理员密码"
                    autoComplete="current-password"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">新密码</label>
                  <Input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) =>
                      setPasswordForm((prev) => ({
                        ...prev,
                        newPassword: e.target.value,
                      }))
                    }
                    placeholder="至少 12 位，避免使用常见弱密码"
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">确认新密码</label>
                  <Input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) =>
                      setPasswordForm((prev) => ({
                        ...prev,
                        confirmPassword: e.target.value,
                      }))
                    }
                    placeholder="再次输入新密码"
                    autoComplete="new-password"
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleUpdateAdminPassword}
                  disabled={isUpdatingPassword}
                >
                  {isUpdatingPassword ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      修改中...
                    </>
                  ) : (
                    '修改密码'
                  )}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* 登录记录 */}
          <TabsContent value="login-history" className="space-y-4">
            {isLoadingLoginHistory ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">登录时间</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">IP地址</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">浏览器</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">状态</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">失败原因</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {loginHistory.map((record) => (
                      <tr key={record.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm">
                          {new Date(record.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono">{record.client_ip}</td>
                        <td className="px-4 py-3 text-sm max-w-xs truncate" title={record.user_agent}>
                          {record.user_agent || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant={record.login_status === 'success' ? 'default' : 'destructive'}>
                            {record.login_status === 'success' ? '成功' : '失败'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {record.fail_reason || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {loginHistory.length === 0 && (
                  <div className="text-center py-10 text-muted-foreground">
                    暂无登录记录
                  </div>
                )}
                {loginHistoryTotal > LOGIN_HISTORY_PAGE_SIZE && (
                  <div className="flex items-center justify-center gap-2 py-4 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={loginHistoryPage <= 1}
                      onClick={() => fetchLoginHistory(loginHistoryPage - 1)}
                    >
                      上一页
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      第 {loginHistoryPage} 页 / 共 {Math.ceil(loginHistoryTotal / LOGIN_HISTORY_PAGE_SIZE)} 页
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={loginHistoryPage >= Math.ceil(loginHistoryTotal / LOGIN_HISTORY_PAGE_SIZE)}
                      onClick={() => fetchLoginHistory(loginHistoryPage + 1)}
                    >
                      下一页
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </div>
      </div>

      {/* 新增会员弹窗 */}
      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新增会员</DialogTitle>
            <DialogDescription>
              填写会员信息，额度规则会按所选会员类型自动设置
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">兑换码</label>
              <Input
                value={newMember.passcode}
                onChange={(e) => setNewMember({ ...newMember, passcode: e.target.value })}
                placeholder="请输入兑换码"
                aria-invalid={Boolean(newMemberPasscodeError)}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                兑换码为 6–32 位数字或英文字母；最外侧空白会自动去除，内部空格、中文和特殊符号不支持。
              </p>
              {newMemberPasscodeError && (
                <p role="alert" className="mt-1 text-xs text-destructive">
                  {newMemberPasscodeError}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">会员类型</label>
              <Select
                value={newMember.member_type}
                onValueChange={(value: string) => 
                  setNewMember({ ...newMember, member_type: value })
                }
                disabled={isLoadingTypeConfigs || memberTypeConfigs.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isLoadingTypeConfigs ? '加载中...' : '请选择会员类型'} />
                </SelectTrigger>
                <SelectContent>
                  {memberTypeConfigs.map((config) => (
                    <SelectItem key={config.id} value={config.name}>
                      {config.name}（{getTypeQuotaLabel(config)}）
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">开通日期</label>
              <Input
                type="date"
                value={newMember.start_date}
                onChange={(e) => setNewMember({ ...newMember, start_date: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">来源渠道</label>
              <Input
                value={newMember.source_channel}
                onChange={(e) => setNewMember({ ...newMember, source_channel: e.target.value })}
                placeholder="请输入来源渠道（可选）"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">备注</label>
              <Input
                value={newMember.remark}
                onChange={(e) => setNewMember({ ...newMember, remark: e.target.value })}
                placeholder="请输入备注（可选）"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMember(false)}>
              取消
            </Button>
            <Button onClick={handleAddMember}>
              创建
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 会员只读详情弹窗 */}
      <Dialog open={isMemberDetailOpen} onOpenChange={setIsMemberDetailOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>会员详情</DialogTitle>
            <DialogDescription>只读查看会员基本信息、当前额度和近期记录；需要修改时进入编辑。</DialogDescription>
          </DialogHeader>
          {isLoadingMemberDetail ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              详情加载中...
            </div>
          ) : memberDetail ? (
            <div className="space-y-5 py-2">
              {/* 基本信息（会员身份核心信息） */}
              <section>
                <h3 className="mb-2 text-sm font-semibold">基本信息</h3>
                <div className="grid gap-3 rounded-lg border bg-slate-50 p-4 sm:grid-cols-2">
                  <div className="min-w-0"><span className="text-muted-foreground">兑换码：</span><span className="break-all font-mono">{memberDetail.member.passcode}</span></div>
                  <div className="min-w-0"><span className="text-muted-foreground">会员类型：</span>{memberDetail.member.member_type}</div>
                  <div className="min-w-0"><span className="text-muted-foreground">来源渠道：</span>{memberDetail.member.source_channel || '-'}</div>
                  <div className="min-w-0"><span className="text-muted-foreground">备注：</span>{memberDetail.member.remark || '-'}</div>
                  <div className="min-w-0"><span className="text-muted-foreground">订单号：</span><span className="break-all font-mono">{memberDetail.member.order_no || '-'}</span></div>
                  {memberDetail.member.batch_id != null && (
                    <div className="min-w-0"><span className="text-muted-foreground">批次：</span>#{memberDetail.member.batch_id} {memberDetail.member.batch_label}</div>
                  )}
                </div>
              </section>

              {/* 状态与有效期 */}
              <section>
                <h3 className="mb-2 text-sm font-semibold">状态与有效期</h3>
                <div className="grid gap-3 rounded-lg border bg-slate-50 p-4 sm:grid-cols-2">
                  <div className="min-w-0"><span className="text-muted-foreground">状态：</span>{MEMBER_STATUS_LABELS[memberDetail.member.status]}</div>
                  <div className="min-w-0"><span className="text-muted-foreground">有效期：</span>
                    {memberDetail.member.status === 'inactive'
                      ? '未激活，用户首次使用后开始计算'
                      : `${memberDetail.member.start_date || '-'} 至 ${memberDetail.member.expiry_date || '-'}`}
                  </div>
                  {memberDetail.member.activated_at && (
                    <div className="min-w-0"><span className="text-muted-foreground">激活时间：</span>{new Date(memberDetail.member.activated_at).toLocaleString()}</div>
                  )}
                  {memberDetail.member.status === 'revoked' && (
                    <div className="min-w-0 text-red-700"><span className="text-muted-foreground">作废：</span>
                      {memberDetail.member.revoked_at ? new Date(memberDetail.member.revoked_at).toLocaleString() : '-'}，原因：{memberDetail.member.revoke_reason || '-'}
                    </div>
                  )}
                </div>
              </section>

              {/* 配额与领取（口径与用户端会员权益一致） */}
              <section>
                <h3 className="mb-2 text-sm font-semibold">配额与领取</h3>
                <div className="grid gap-3 rounded-lg border bg-slate-50 p-4 sm:grid-cols-2">
                  <div className="min-w-0"><span className="text-muted-foreground">配额模式：</span>{memberDetail.member.quota_mode === 'total' ? '年度总额度' : '按月配额'}</div>
                  {memberDetail.member.quota_mode !== 'total' && (
                    <div className="min-w-0"><span className="text-muted-foreground">预支下周期：</span>
                      {(memberDetail.member.next_cycle_borrowed ?? 0) > 0
                        ? `已预支 ${memberDetail.member.next_cycle_borrowed} 次，已用 ${getMemberUsedBorrowedQuota(memberDetail.member)} 次（下周期扣已用的部分）`
                        : '未预支'}
                    </div>
                  )}
                  <div className="min-w-0"><span className="text-muted-foreground">周期/总额度：</span>{memberDetail.member.quota_mode === 'total' ? `${memberDetail.member.total_quota ?? 0} 次` : `${memberDetail.member.monthly_quota} 次/月`}</div>
                  <div className="min-w-0"><span className="text-muted-foreground">已用：</span>{getMemberQuotaUsageLabel(memberDetail.member)}</div>
                  <div className="min-w-0"><span className="text-muted-foreground">剩余：</span>{getMemberRemainingLabel(memberDetail.member)}</div>
                  <div className="min-w-0"><span className="text-muted-foreground">今日已领：</span>{memberDetail.member.today_used}/{memberDetail.member.daily_limit}</div>
                  <div className="min-w-0"><span className="text-muted-foreground">累计领取：</span>{memberDetail.member.total_redemptions}</div>
                  <div className="min-w-0 sm:col-span-2"><span className="text-muted-foreground">周期范围/恢复：</span>{getMemberCycleRangeLabel(memberDetail.member)}</div>
                  <div className="min-w-0 sm:col-span-2"><span className="text-muted-foreground">最后成功领取：</span>{memberDetail.member.last_redemption_date || '暂无'}</div>
                </div>
              </section>

              <div>
                <h3 className="mb-3 text-sm font-semibold">近期记录</h3>
                {memberDetail.recent_records.length === 0 ? (
                  <p className="rounded-lg border px-4 py-8 text-center text-sm text-muted-foreground">暂无记录</p>
                ) : (
                  <div className="space-y-2">
                    {memberDetail.recent_records.map((record) => (
                      <div key={record.id} className="rounded-lg border px-4 py-3 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium">{record.record_type === 'quota_adjustment' ? '管理员调整' : record.topic_name}</span>
                          <span className="text-xs text-muted-foreground">{new Date(record.redemption_time).toLocaleString()}</span>
                        </div>
                        {record.record_type === 'quota_adjustment' ? (
                          <div className="mt-2 space-y-1 text-muted-foreground">
                            <p>额度：{record.quota_before?.cycle_used ?? 0} → {record.quota_after?.cycle_used ?? 0}（周期），{record.quota_before?.today_used ?? 0} → {record.quota_after?.today_used ?? 0}（今日），{record.quota_before?.total_redemptions ?? 0} → {record.quota_after?.total_redemptions ?? 0}（累计）</p>
                            {(record.quota_before?.next_cycle_borrowed ?? 0) !== (record.quota_after?.next_cycle_borrowed ?? 0) && (
                              <p>预支下周期：{record.quota_before?.next_cycle_borrowed ?? 0} → {record.quota_after?.next_cycle_borrowed ?? 0} 次</p>
                            )}
                            <p>说明：{record.adjustment_note || '未填写调整说明'}</p>
                          </div>
                        ) : (
                          <p className="mt-1 text-muted-foreground">{record.result === 'success' ? '领取成功' : '领取失败：' + (record.fail_reason || '未说明原因')}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMemberDetailOpen(false)}>关闭</Button>
            {memberDetail && (
              <>
                {memberDetail.member.status !== 'revoked' && (
                  <Button variant="outline" className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800 sm:mr-auto" onClick={() => {
                    setIsMemberDetailOpen(false);
                    setRevokingDetail(memberDetail);
                  }}>
                    <Ban className="mr-1 h-4 w-4" />
                    退款作废
                  </Button>
                )}
                <Button variant="outline" onClick={() => {
                  setIsMemberDetailOpen(false);
                  handleOpenEditMember(memberDetail.member);
                }}>
                  编辑资料
                </Button>
                {!isSystemStatus(memberDetail.member.status) && (
                  <Button onClick={() => {
                    setIsMemberDetailOpen(false);
                    setAdjustingMember(memberDetail.member);
                  }}>
                    调整额度
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 编辑会员资料弹窗 */}
      <Dialog open={!!editingMember} onOpenChange={(open) => {
        if (!open) setEditingMember(null);
      }}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>编辑资料</DialogTitle>
            <DialogDescription className="break-all font-mono">
              {editingMember?.passcode}
            </DialogDescription>
          </DialogHeader>
          {editingMember && (
            <div className="space-y-4 py-4">
              {isSystemStatus(editingMember.status) ? (
                <div>
                  <span className="text-sm font-medium mb-2 block">状态</span>
                  <p className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
                    {MEMBER_STATUS_LABELS[editingMember.status]}
                    {editingMember.status === 'inactive' ? '：用户首次使用后自动启用，不能手动修改' : '：作废后不能恢复'}
                  </p>
                </div>
              ) : (
              <div>
                <label className="text-sm font-medium mb-2 block">状态</label>
                <Select
                  value={editingMember.status}
                  onValueChange={(value: 'active' | 'frozen' | 'expired') =>
                    setEditingMember({ ...editingMember, status: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">启用</SelectItem>
                    <SelectItem value="frozen">冻结</SelectItem>
                    <SelectItem value="expired">已过期</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              )}
              <div>
                <label htmlFor="edit-member-order-no" className="text-sm font-medium mb-2 block">订单号</label>
                <Input
                  id="edit-member-order-no"
                  value={editingOrderNo}
                  maxLength={64}
                  onChange={(e) => setEditingOrderNo(e.target.value)}
                  placeholder="小红书订单号（可选），清空即删除"
                  className="font-mono"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">来源渠道</label>
                <Input
                  value={editingMember.source_channel || ''}
                  onChange={(e) => 
                    setEditingMember({ ...editingMember, source_channel: e.target.value })
                  }
                  placeholder="请输入来源渠道（可选）"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">备注</label>
                <Input
                  value={editingMember.remark || ''}
                  onChange={(e) => 
                    setEditingMember({ ...editingMember, remark: e.target.value })
                  }
                  placeholder="请输入备注（可选）"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingMember(null)}>
              取消
            </Button>
            <Button onClick={handleUpdateMember} disabled={isUpdatingMember}>
              {isUpdatingMember ? '保存中…' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 调整额度弹窗 */}
      <QuotaAdjustDialog
        member={adjustingMember}
        onClose={() => setAdjustingMember(null)}
        onSaved={fetchMembers}
      />

      {/* 退款作废弹窗 */}
      <RevokeMemberDialog
        detail={revokingDetail}
        onClose={() => setRevokingDetail(null)}
        onRevoked={fetchMembers}
      />

      {/* 新增会员类型弹窗 */}
      <Dialog open={showAddTypeConfig} onOpenChange={setShowAddTypeConfig}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新增{typeConfigTab === 'total' ? '年度总次数' : '按月配额'}会员类型</DialogTitle>
            <DialogDescription>
              {typeConfigTab === 'total'
                ? '配置年度总次数类型，会员在有效期内可领取指定次数，不限单日/单月'
                : '配置按月配额类型，设置每月限额和单日上限'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">类型名称</label>
              <Input
                value={newTypeConfig.name}
                onChange={(e) => setNewTypeConfig({ ...newTypeConfig, name: e.target.value })}
                placeholder="如：轻享年卡、畅享年卡"
              />
            </div>
            {typeConfigTab === 'monthly' ? (
              <>
                <div>
                  <label className="text-sm font-medium mb-2 block">月配额（次/月）</label>
                  <Input
                    type="number"
                    min={1}
                    value={newTypeConfig.monthly_quota}
                    onChange={(e) => setNewTypeConfig({ ...newTypeConfig, monthly_quota: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">单日上限（次/日）</label>
                  <Input
                    type="number"
                    min={1}
                    value={newTypeConfig.daily_limit}
                    onChange={(e) => setNewTypeConfig({ ...newTypeConfig, daily_limit: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="text-sm font-medium mb-2 block">年度总次数（次/年）<span className="text-red-500">*</span></label>
                  <Input
                    type="number"
                    min={1}
                    placeholder="请输入年度总次数"
                    value={newTypeConfig.total_quota}
                    onChange={(e) => setNewTypeConfig({ ...newTypeConfig, total_quota: e.target.value === '' ? '' : (parseInt(e.target.value) || 0) })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">单日配额（次/日）<span className="text-red-500">*</span></label>
                  <Input
                    type="number"
                    min={1}
                    placeholder="请输入单日配额"
                    value={newTypeConfig.daily_limit}
                    onChange={(e) => setNewTypeConfig({ ...newTypeConfig, daily_limit: e.target.value === '' ? '' : (parseInt(e.target.value) || 0) })}
                  />
                </div>
              </>
            )}
            <div>
              <label className="text-sm font-medium mb-2 block">有效期（月）</label>
              <Input
                type="number"
                min={1}
                value={newTypeConfig.validity_months}
                onChange={(e) => setNewTypeConfig({ ...newTypeConfig, validity_months: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTypeConfig(false)}>
              取消
            </Button>
            <Button onClick={handleAddTypeConfig}>
              创建
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 编辑会员类型弹窗 */}
      <Dialog open={!!editingTypeConfig} onOpenChange={(open) => !open && setEditingTypeConfig(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              编辑{(editingTypeConfig?.quota_mode || 'monthly') === 'total' ? '年度总次数' : '按月配额'}类型
            </DialogTitle>
            <DialogDescription>
              修改会员类型配置
            </DialogDescription>
          </DialogHeader>
          {editingTypeConfig && (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">类型名称</label>
                <Input
                  value={editingTypeConfig.name}
                  onChange={(e) => setEditingTypeConfig({ ...editingTypeConfig, name: e.target.value })}
                />
              </div>
              {(editingTypeConfig.quota_mode || 'monthly') === 'monthly' ? (
                <>
                  <div>
                    <label className="text-sm font-medium mb-2 block">月配额（次/月）</label>
                    <Input
                      type="number"
                      min={1}
                      value={editingTypeConfig.monthly_quota}
                      onChange={(e) => setEditingTypeConfig({ ...editingTypeConfig, monthly_quota: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">单日上限（次/日）</label>
                    <Input
                      type="number"
                      min={1}
                      value={editingTypeConfig.daily_limit}
                      onChange={(e) => setEditingTypeConfig({ ...editingTypeConfig, daily_limit: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="text-sm font-medium mb-2 block">年度总次数（次/年）</label>
                    <Input
                      type="number"
                      min={1}
                      value={editingTypeConfig.total_quota || 0}
                      onChange={(e) => setEditingTypeConfig({ ...editingTypeConfig, total_quota: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">单日配额（次/日）</label>
                    <Input
                      type="number"
                      min={1}
                      value={editingTypeConfig.daily_limit}
                      onChange={(e) => setEditingTypeConfig({ ...editingTypeConfig, daily_limit: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </>
              )}
              <div>
                <label className="text-sm font-medium mb-2 block">有效期（月）</label>
                <Input
                  type="number"
                  min={1}
                  value={editingTypeConfig.validity_months}
                  onChange={(e) => setEditingTypeConfig({ ...editingTypeConfig, validity_months: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">状态</label>
                <Select
                  value={editingTypeConfig.status}
                  onValueChange={(value: '启用' | '停用') =>
                    setEditingTypeConfig({ ...editingTypeConfig, status: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="启用">启用</SelectItem>
                    <SelectItem value="停用">停用</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTypeConfig(null)}>
              取消
            </Button>
            <Button onClick={handleUpdateTypeConfig}>
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除会员类型确认对话框 */}
      <Dialog open={!!deletingTypeConfig} onOpenChange={() => setDeletingTypeConfig(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认停用</DialogTitle>
            <DialogDescription>
              确定要停用会员类型 "{deletingTypeConfig?.name}" 吗？停用后该会员类型将无法使用。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingTypeConfig(null)}>
              取消
            </Button>
            <Button variant="destructive" onClick={confirmDeleteTypeConfig}>
              确认停用
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
};

export default AdminDashboardPage;

// 客服配置的实时预览：按家长端客服弹窗的样子显示当前填写的内容（未保存也会更新）
function ContactPreview({ config }: { config: ContactConfig }) {
  const wechatId = config.wechat_id?.trim();
  const qr = config.qr_image_url?.trim();
  return (
    <aside className="space-y-2 xl:sticky xl:top-6">
      <p className="text-[13px] font-semibold text-muted-foreground">家长看到的效果（未保存也会实时更新）</p>
      {!config.enabled ? (
        <div className="rounded-2xl border border-dashed bg-white p-6 text-center text-sm text-muted-foreground">
          前台客服入口已关闭，家长看不到客服按钮
        </div>
      ) : config.method === 'link' ? (
        <div className="space-y-3 rounded-2xl bg-white p-5 text-center shadow-[0_0_0_1px_var(--border)]">
          <p className="text-sm text-muted-foreground">家长点击「{config.button_text || '联系客服'}」后直接打开：</p>
          <p className="break-all font-mono text-sm text-foreground">{config.link_url || '（未填写链接）'}</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2.5 rounded-2xl bg-white px-5 py-5 text-center shadow-[0_0_0_1px_var(--border),0_10px_30px_rgba(80,50,20,0.08)]">
          <p className="text-base font-semibold text-foreground">{config.title || '联系客服'}</p>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {config.description || '支持购买、续费、单独获取主题与找回兑换码'}
          </p>
          {qr ? (
            <img src={qr} alt="客服二维码预览" className="h-36 w-36 rounded-lg object-contain" />
          ) : (
            <div className="flex h-36 w-36 items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground">
              暂未配置二维码图片
            </div>
          )}
          {wechatId && (
            <div className="flex w-full items-center justify-between gap-2 rounded-xl bg-background px-3 py-2">
              <span className="truncate font-mono text-sm">{wechatId}</span>
              <span className="shrink-0 rounded-md bg-cta px-2.5 py-1 text-xs font-semibold text-cta-foreground">复制</span>
            </div>
          )}
          <p className="pt-1 text-[11px] text-muted-foreground">
            按钮文案：{config.button_text || '联系客服'} · 兑换码提示：{config.passcode_hint || '（空）'}
          </p>
        </div>
      )}
    </aside>
  );
}
